import {
  Button,
  Combobox,
  Heading,
  Link,
  Spinner,
  Tooltip,
} from "@ttc3k/trekker";
import { ArrowRight } from "iconoir-react";
import { VerifiedBadge } from "shared/components/atoms";
import { CopyToClipboard } from "shared/components/molecules";
import { Box, Center, Flex, styled } from "styled-system/jsx";
import {
  usePersonHeaderCard,
  UsePersonHeaderCardProps,
} from "./usePersonHeaderCard";
import { useLoggedInAs } from "shared/hooks";
import {
  EnumOwnerType,
  PersonDetailsQuery,
  type MembershipBySubjectAndOrganizationQuery,
  useOrganizationByIdQuery,
} from "gql/generated";
import { gql, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { defaultHiddenEmailStart } from "shared/utils";
import { useFeatureFlag } from "shared/contextProviders";
import { FeatureFlags } from "shared/featureFlags";
import { RenewalDateLine } from "shared/components/organisms/BackOfficeTags/RenewalDateLine";

const MEMBERSHIP_DISPLAY_FOR_PERSON = gql`
  query MembershipDisplayForPerson(
    $personId: MongoID!
    $organizationId: MongoID!
  ) {
    membershipDisplayForPerson(
      personId: $personId
      organizationId: $organizationId
    ) {
      source
      listingEntityId
      listingName
      membership {
        _id
        status
        tierTagId
        statusTagIds
        currentPeriodEnd
        nextRenewalDate
        manualRenewalDate
        renewalAnchorDate
        renewalState {
          phase
          paidThroughDate
          nextRenewalDate
          displayDate
        }
      }
    }
  }
`;

type MembershipForRenewal = NonNullable<
  MembershipBySubjectAndOrganizationQuery["membershipBySubjectAndOrganization"]
>;

type MembershipDisplayForPersonResult = {
  membershipDisplayForPerson?: {
    source: string;
    listingEntityId?: string | null;
    listingName?: string | null;
    membership?: MembershipForRenewal | null;
  } | null;
};

type PersonHeaderCardProps = UsePersonHeaderCardProps & {
  person: PersonDetailsQuery["personDetails"];
  onContactInfoClick: () => void;
  onTagsManageClick: () => void;
};

export const PersonHeaderCard = ({
  person,
  onContactInfoClick,
  onTagsManageClick,
}: PersonHeaderCardProps) => {
  const { t } = useTranslation("people");
  const loggedInAs = useLoggedInAs();

  const {
    canAssignTags,
    canManageTags,
    updatingTagsForPersonLoading,
    handleTagsChange,
    filterableTagsCollection,
    handleTagInputValueSearch,
    handleComboboxOpenChange,
  } = usePersonHeaderCard({ person });

  // Memoize the selected tags to avoid re-rendering the combobox when the tags are not changed
  const selectedTags = useMemo(() => {
    return _.orderBy(person?.personInfo?.tags || [], "name.en", "asc").map(
      tag => tag._id,
    );
  }, [person?.personInfo?.tags]);

  // Show verified badge only for people with an active Whereabouts account (Enrolled = has logged in)
  const isVerified = useMemo(
    () => !!person?.isUserClaimed,
    [person?.isUserClaimed],
  );

  const email = person?.personInfo?.email;

  const renewalEnabled = useFeatureFlag(FeatureFlags.MembershipRenewal);
  const organizationId =
    loggedInAs?.type === EnumOwnerType.Organization
      ? loggedInAs.entityId
      : undefined;

  const { data: orgData } = useOrganizationByIdQuery({
    variables: { id: organizationId ?? "" },
    skip: !organizationId || !renewalEnabled,
  });
  const renewalSettings = orgData?.organizationById?.metadata?.renewalSettings;

  const personId = person?._id;

  const { data: membershipDisplayData } =
    useQuery<MembershipDisplayForPersonResult>(MEMBERSHIP_DISPLAY_FOR_PERSON, {
      variables: {
        personId: personId ?? "",
        organizationId: organizationId ?? "",
      },
      skip: !personId || !organizationId || !renewalEnabled,
    });

  const membershipDisplay =
    membershipDisplayData?.membershipDisplayForPerson;
  const membership = membershipDisplay?.membership;
  const isListingMembership = membershipDisplay?.source === "OPERATOR";

  if (!person || !person.personInfo) return null;

  return (
    <Flex
      flexDir="column"
      gap="300"
      p="400"
      bgColor="white"
      borderRadius="150"
      boxShadow="0 10px 15px -5px rgba(10, 10, 10, 0.10), 0 5px 5px -2px rgba(10, 10, 10, 0.05)"
    >
      <Flex flexDir="column" gap="100">
        {/* Name and verified badge */}
        <Flex justify="space-between" align="center" gap="300">
          <Heading size="h4" lineClamp="2" wordBreak="break-all">
            {person.personInfo.firstName} {person.personInfo.lastName}
          </Heading>
          {isVerified && (
            <Tooltip
              content={t("HEADER.VERIFIED_BADGE_TOOLTIP")}
              contentProps={{
                textProps: {
                  visual: "smallRegular",
                },
              }}
              positioning={{
                placement: "top-end",
                offset: { mainAxis: 18, crossAxis: 18 },
              }}
            >
              <VerifiedBadge w="32px" h="32px" flexShrink={0} />
            </Tooltip>
          )}
        </Flex>

        {/* Email address and copy to clipboard */}
        {!email?.startsWith(defaultHiddenEmailStart) && (
          <Flex align="center" gap="50" w="full">
            <Link
              href={`mailto:${email}`}
              color="text.mid"
              textDecoration="underline"
              _hover={{
                color: "text.dark",
              }}
              _focusVisible={{
                color: "text.dark",
              }}
              style={{
                transition: "color 0.2s ease-in-out",
              }}
              lineClamp={1}
              wordBreak="break-all"
            >
              {email}
            </Link>
            <CopyToClipboard iconOnly text={email} iconCopyColor="icon.mid" />
          </Flex>
        )}

        {/* Edit person details button ... to open the modal */}
        <Button
          visual="unstyled"
          Icon={ArrowRight}
          color="text.accent.blue.mid"
          _hover={{
            color: "text.accent.blue.dark",
          }}
          _focusVisible={{
            color: "text.accent.blue.dark",
          }}
          isIconRightAligned={true}
          size="sm"
          w="fit-content"
          onClick={onContactInfoClick}
        >
          <styled.span fontSize="md">
            {t("PERSON_DETAILS_MODAL.EDIT_CONTACT_INFO")}
          </styled.span>
        </Button>
      </Flex>
      {/* Person tags */}
      {canAssignTags && (
        <Combobox.Root
          value={selectedTags}
          collection={filterableTagsCollection}
          multiple
          onValueChange={d => {
            if (!updatingTagsForPersonLoading) {
              handleTagsChange(d.value);
            }
          }}
          onInputValueChange={d => handleTagInputValueSearch(d.inputValue)}
          onOpenChange={d => handleComboboxOpenChange(d.open)}
          placeholder={t("HEADER.TAGS.PLACEHOLDER")}
          tagVisualVariant="accent.purple"
          tagModeVariant="dark"
          aria-label="Add or remove tags"
          disabled={updatingTagsForPersonLoading}
        >
          {filterableTagsCollection.items.map(tag => (
            <Box key={tag.id} position="relative">
              <Combobox.Item item={tag} label={tag.label} />
              {updatingTagsForPersonLoading && (
                <Center
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="white"
                  opacity="0.5"
                >
                  <Spinner w="24px" h="24px" />
                </Center>
              )}
            </Box>
          ))}
          {/* The manage tags button */}
          {canManageTags && (
            <Box
              borderTop="1px solid"
              borderColor="border.light"
              py="150"
              px="100"
            >
              <Button
                visual="unstyled"
                color="text.accent.blue.mid"
                _hover={{
                  color: "text.accent.blue.dark",
                }}
                _focusVisible={{
                  color: "text.accent.blue.dark",
                }}
                style={{
                  transition: "color 0.2s ease-in-out",
                }}
                w="full"
                justifyContent="flex-start"
                disabled={updatingTagsForPersonLoading}
                onClick={onTagsManageClick}
              >
                {t("HEADER.TAGS.CTA_BUTTON_COPY")}
              </Button>
            </Box>
          )}
        </Combobox.Root>
      )}
      {renewalEnabled &&
        membership &&
        (membership.tierTagId || isListingMembership) && (
          <RenewalDateLine
            membership={membership}
            renewalSettings={renewalSettings}
            readOnly
            attributionSource={isListingMembership ? "listing" : undefined}
            listingName={membershipDisplay?.listingName}
          />
        )}
    </Flex>
  );
};
